import api, { ApiResponse } from '../../../services/api';
import { Turn, ScenarioBriefingData } from '../types';
import { AttemptVoiceContract } from './voice-contract';

export type { AttemptVoiceContract };

/**
 * Voice fields the backend resolved for the attempt. Attempt startup is the single
 * authority: the client renders these values and must not re-resolve voice, engine
 * or gender. They are optional only because legacy responses predate the contract.
 */
export interface EffectiveVoiceFields {
  effectiveTtsVoice?: string;
  effectiveTtsProvider?: string;
  effectiveGenderPreference?: string;
  /** Legacy alias of `effectiveTtsProvider`, still emitted for older clients. */
  effectiveTTSProvider?: string;
  voiceContract?: AttemptVoiceContract;
}

export interface StartAttemptResponse extends EffectiveVoiceFields {
  attemptId: string;
  scenarioId: string;
  status: string;
  stageIndex?: number;
  initialTurn?: {
    speaker: 'PERSONA';
    text: string;
    audioUrl?: string;
    personaName?: string;
    speakerName?: string;
    ttsVoice?: string;
    ttsProvider?: string;
    genderPreference?: string;
    country?: string;
    tone?: string;
    speakingPace?: number;
    pitchPreference?: number;
  };
  openingUtterance?: string;
  openingAudioBase64?: string;
  openingMimeType?: string;
  scenario?: ScenarioBriefingData;
}

export interface TurnResponse extends EffectiveVoiceFields {
  replyText?: string;
  response?: string;
  audioUrl?: string;
  stageIndex?: number;
  turns?: Turn[];
  aiTurn?: Turn;
  data?: {
    replyText?: string;
    audioUrl?: string;
    turns?: Turn[];
  };
}

export interface AttemptStateResponse extends EffectiveVoiceFields {
  attemptId: string;
  scenarioId: string;
  status: string;
  turns: Turn[];
  stageIndex: number;
  scenarioConfig?: ScenarioBriefingData;
  scenario?: ScenarioBriefingData;
}

export const simulationService = {
  /**
   * Fetch briefing dossier for a scenario
   */
  async getBriefing(scenarioId: string): Promise<ApiResponse<ScenarioBriefingData>> {
    return api.simulations.getBriefing(scenarioId) as Promise<ApiResponse<ScenarioBriefingData>>;
  },

  /**
   * Start a new attempt for a scenario
   */
  async startAttempt(scenarioId: string, language = 'en-US'): Promise<ApiResponse<StartAttemptResponse>> {
    return api.simulations.startAttempt(scenarioId, { language }) as Promise<ApiResponse<StartAttemptResponse>>;
  },

  /**
   * Resume an existing in-progress attempt
   */
  async resumeAttempt(attemptId: string, sessionToken?: string): Promise<ApiResponse<AttemptStateResponse>> {
    return api.attempts.resume(attemptId, { sessionToken }) as Promise<ApiResponse<AttemptStateResponse>>;
  },

  /**
   * Send heartbeat to keep attempt lease active
   */
  async sendHeartbeat(attemptId: string, sessionToken?: string): Promise<ApiResponse<{ heartbeatRecordedAt: string }>> {
    return api.attempts.heartbeat(attemptId, sessionToken) as Promise<ApiResponse<{ heartbeatRecordedAt: string }>>;
  },

  /**
   * Get current attempt snapshot state
   */
  async getAttemptState(attemptId: string): Promise<ApiResponse<AttemptStateResponse>> {
    return api.attempts.getState(attemptId) as Promise<ApiResponse<AttemptStateResponse>>;
  },

  /**
   * Submit a learner utterance turn to the dialogue runtime.
   * `speechMeta` carries client-measured speech telemetry (speaking time, hesitation,
   * STT confidence and any barge-in/interruption event) so the evaluation engine can
   * score filler words, pauses and interruptions from real data. The recorded utterance
   * audio travels with the request — the server stores a media URL, never a binary.
   */
  async submitTurn(
    attemptId: string,
    learnerUtterance: string,
    speechMeta?: import('../hooks/useSimulationAudio').LearnerTurnSpeechMeta
  ): Promise<ApiResponse<TurnResponse>> {
    const interruption = speechMeta?.interruption || null;
    const mapEvent = (ev: NonNullable<typeof interruption>) => ({
      occurredAt: ev.occurredAt,
      timestamp: ev.occurredAt,
      aiSpeechStartedAt: ev.aiSpeechStartedAt,
      durationMs: ev.durationMs,
      speaker: 'LEARNER',
      interruptedSpeaker: ev.interruptedSpeaker,
      interruptedRole: 'PERSONA',
      source: ev.source,
    });
    // The recorder finalizes asynchronously; a stuck capture must never delay the
    // persona's reply beyond a short grace window (150ms).
    const audioBase64 = speechMeta?.audioPromise
      ? await Promise.race<string | null>([
          speechMeta.audioPromise.catch(() => null),
          new Promise<string | null>((resolve) => setTimeout(() => resolve(null), 150)),
        ])
      : null;

    return api.attempts.processTurn(attemptId, learnerUtterance, {
      sttConfidence: speechMeta?.sttConfidence,
      audioBase64: audioBase64 || undefined,
      interruption: interruption ? mapEvent(interruption) : null,
      // All cut-ins observed while the persona held the floor, not just the last one
      interruptions: (speechMeta?.interruptions || []).map(mapEvent),
      speechDurationMs: speechMeta?.durationMs,
      hesitationMs: speechMeta?.hesitationMs,
      wordCount: speechMeta?.wordCount,
      speechStartMs: speechMeta?.speechStartMs,
      speechEndMs: speechMeta?.speechEndMs,
    }) as Promise<ApiResponse<TurnResponse>>;
  },

  /**
   * Complete and evaluate an attempt
   */
  async completeAttempt(attemptId: string, reason = 'LEARNER_CONFIRMED', turns?: Turn[], elapsedSeconds?: number): Promise<ApiResponse<unknown>> {
    return api.attempts.submit(attemptId, reason, turns, elapsedSeconds);
  },

  /**
   * Transcribe recorded audio with server STT fallback
   */
  async transcribeAudio(audioBase64: string, language = 'en'): Promise<ApiResponse<{ transcript: string }>> {
    return api.stt.transcribe(audioBase64, language) as Promise<ApiResponse<{ transcript: string }>>;
  },
};
